import React from "react";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { resolveValue, toast, useToaster } from "../../utils/toast";
import type { Toast } from "../../utils/toast";

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const getToastTone = (type: Toast["type"]) => {
  switch (type) {
    case "success":
      return {
        icon: CheckCircleIcon,
        iconClass: "text-emerald-600",
        borderClass: "border-emerald-200",
      };
    case "error":
      return {
        icon: ExclamationCircleIcon,
        iconClass: "text-red-600",
        borderClass: "border-red-200",
      };
    case "loading":
      return {
        icon: ArrowPathIcon,
        iconClass: "text-blue-600 animate-spin",
        borderClass: "border-blue-200",
      };
    default:
      return {
        icon: InformationCircleIcon,
        iconClass: "text-slate-500",
        borderClass: "border-slate-200",
      };
  }
};

const AppToast: React.FC<{ item: Toast }> = ({ item }) => {
  const { t } = useTranslation();

  if (item.type === "custom") {
    return <>{resolveValue(item.message, item)}</>;
  }

  const tone = getToastTone(item.type);
  const Icon = tone.icon;
  const message = resolveValue(item.message, item);

  return (
    <div
      className={cx(
        "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 shadow-lg ring-1 ring-black/5 transition duration-200 ease-out",
        item.visible
          ? "translate-y-0 opacity-100"
          : "-translate-y-2 opacity-0",
        tone.borderClass,
        item.className
      )}
      role={item.ariaProps.role}
      aria-live={item.ariaProps["aria-live"]}
    >
      {item.icon ? (
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
          {item.icon}
        </span>
      ) : (
        <Icon className={cx("mt-0.5 h-5 w-5 shrink-0", tone.iconClass)} />
      )}
      <div className="min-w-0 flex-1 break-words leading-5">{message}</div>
      <button
        type="button"
        onClick={() => toast.dismiss(item.id)}
        className="ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={t("common:dismissNotification")}
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export const AppToaster: React.FC = () => {
  const { toasts, handlers } = useToaster({
    duration: 4000,
    success: { duration: 3000 },
    error: { duration: 5000 },
  });

  return (
    <div
      className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2 sm:right-6 sm:top-6"
      onMouseEnter={handlers.startPause}
      onMouseLeave={handlers.endPause}
    >
      {toasts.map((item) => (
        <AppToast key={item.id} item={item} />
      ))}
    </div>
  );
};
